// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parseBoolean } from "@awc/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadOrderByOrderNumber from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrderByOrderNumber";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import OrderConfirmationBillingInformation, {
    OrderConfirmationBillingInformationStyles,
} from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationBillingInformation";
import OrderConfirmationShippingInformation, {
    OrderConfirmationShippingInformationStyles,
} from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationShippingInformation";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// will have only order date and order number

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCartState(state, state.pages.orderConfirmation.cartId);
    return {
        cartState,
        billToState: getBillToState(state, cartState.value?.billToId),
        shipToState: getShipToState(state, cartState.value?.shipToId),
        orderState: getOrderState(state, cartState.value?.orderNumber),
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        language: state.context.session.language,
    };
};

const mapDispatchToProps = {
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
};

export interface OrderConfirmationOrderInformationStyles {
    OrderInformationGridContainer?: GridContainerProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeading?: TypographyProps;
    orderNumberText?: TypographyPresentationProps;
    shippingInformationGridItem?: GridItemProps;
    vatNumberGridItem?: GridItemProps;
    vatNumberTitleText?: TypographyPresentationProps;
    vatNumberText?: TypographyPresentationProps;
    orderConfirmationShippingInformation?: OrderConfirmationShippingInformationStyles;
    billingInformationGridItem?: GridItemProps;
    orderConfirmationBillingInformation?: OrderConfirmationBillingInformationStyles;
    notesGridItem?: GridItemProps;
    notesTitle: TypographyProps;
    notesDescription?: TypographyPresentationProps;
    orderDetailsGridContainer?: GridContainerProps;
    orderInformationGridItems?: GridItemProps;
    informationGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    orderDateGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    salespersonGridItem?: GridItemProps;
    dueDateGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
    orderNotesGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    // XNG-Change
    dateRequestedTitle?: TypographyPresentationProps;
    dateRequestedText?: TypographyPresentationProps;
    billingInformationCreditCardAddressGridItem?: GridItemProps;
    billingInformationCreditCardAddressTitle?: TypographyPresentationProps;
    shippingOtherInformationGridItem?: GridItemProps;
    shippingOtherInformationGridContainer?: GridContainerProps;
    shippingInformationCarrierGridItem?: GridItemProps;
    shippingServiceTitle?: TypographyPresentationProps;
    shippingCarrierDescription?: TypographyPresentationProps;
    shippingInformationDateRequestedGridItem?: GridItemProps;
    billingCreditCardAddressTitle?: TypographyPresentationProps;
    orderInformationGridItem?: GridItemProps;
    createAccountGridItem?: GridItemProps;
    shippingTitle?: TypographyPresentationProps;
    shippingAndBillingGridItem?: GridItemProps;
    shippingAccountNum?: SmallHeadingAndTextStyles;
}

export const orderInformationStyles: OrderConfirmationOrderInformationStyles = {
    OrderInformationGridContainer: {
        gap: 10,
    },
    orderNumberGridItem: {
        width: 12,
    },
    orderNumberHeading: {
        variant: "h2",
        as: "h1",
        css: css`
            margin-bottom: 5px;
        `,
    },
    shippingInformationGridItem: {
        width: 12,
    },
    vatNumberGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    vatNumberTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    billingInformationGridItem: {
        width: 12,
    },
    notesGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    notesTitle: {
        variant: "h6",
        as: "h2",
        css: css`
            margin-bottom: 5px;
        `,
    },
    headingAndTextStyles: {
        heading: {
            variant: "h6",
            as: "h2",
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
    },
    orderInformationGridItems: {
        css: css`
            @media print {
                padding: 5px !important;
            }
        `,
    },
    statusGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    orderDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    poNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    salespersonGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    dueDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    billingAddressGridItem: {
        width: 6,
    },
    shippingAddressGridItem: {
        width: 6,
    },
    orderNotesGridItem: {
        width: 12,
    },
    orderDetailsGridContainer: {
        css: css`
            padding-left: 5px;
            padding-bottom: 15px;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = orderInformationStyles;

const OrderConfirmationOrderInformation = ({
    cartState,
    billToState,
    shipToState,
    orderState,
    pickUpWarehouse,
    enableVat,
    language,
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
}: Props) => {
    useEffect(() => {
        if (cartState.isLoading || !cartState.value) {
            return;
        }

        const cart = cartState.value;
        if (!billToState.isLoading && !billToState.value && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }

        if (!shipToState.isLoading && !shipToState.value && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }

        if (!orderState.isLoading && !orderState.value && cart.orderNumber && cart.status === "Submitted") {
            loadOrderByOrderNumber({ orderNumber: cart.orderNumber });
        }
    }, [cartState]);

    const cart = cartState.value;
    const billTo = billToState.value;
    const shipTo = shipToState.value;
    const order = orderState.value;
    if (!cart || !billTo || !shipTo) {
        return null;
    }

    // XNG-Change: changes on structure to match classic
    return (
        <GridContainer {...styles.OrderInformationGridContainer}>
            <GridItem {...styles.orderInformationGridItem}>
                <GridContainer gap={12}>
                    <GridItem {...styles.orderNumberGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Order #")}
                            text={cart.erpOrderNumber || cart.orderNumber}
                        />
                    </GridItem>
                    {order?.statusDisplay && (
                        <GridItem {...mergeToNew(styles.orderInformationGridItems, styles.statusGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Status")}
                                // XNG-Change: remove || cart.statusDisplay
                                text={order.statusDisplay}
                            />
                        </GridItem>
                    )}

                    <GridItem {...mergeToNew(styles.orderInformationGridItems, styles.orderDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Order Date")}
                            text={
                                order?.orderDate
                                    ? getLocalizedDateTime({
                                          dateTime: new Date(order.orderDate as Date),
                                          language,
                                      })
                                    : ""
                            }
                        />
                    </GridItem>
                    {!!cart.poNumber && (
                        <GridItem {...mergeToNew(styles.orderInformationGridItems, styles.poNumberGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                // XNG-Change: change label
                                heading={translate("PO #")}
                                text={cart.poNumber}
                            />
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>

            <GridItem {...styles.createAccountGridItem}>
                {createWidgetElement("OrderConfirmation/OrderConfirmationAccountSignUpMessage", {
                    fields: {},
                })}
            </GridItem>
            {/* {cart?.salespersonName && (
                    <GridItem {...mergeToNew(styles.orderInformationGridItems, styles.salespersonGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Salesperson")}
                            text={cart.salespersonName}
                        />
                    </GridItem>
                )} */}
            <GridItem {...styles.shippingAndBillingGridItem}>
                <GridContainer gap={24}>
                    <GridItem {...styles.billingInformationGridItem}>
                        <OrderConfirmationBillingInformation
                            cart={cart}
                            billTo={billTo}
                            extendedStyles={styles.orderConfirmationBillingInformation}
                        />
                    </GridItem>
                    <GridItem {...styles.shippingInformationGridItem}>
                        <OrderConfirmationShippingInformation
                            cart={cart}
                            shipTo={shipTo}
                            pickUpWarehouse={pickUpWarehouse}
                            extendedStyles={styles.orderConfirmationShippingInformation}
                        />
                    </GridItem>
                    {enableVat && cart.customerVatNumber && (
                        <GridItem {...styles.vatNumberGridItem}>
                            <Typography {...styles.vatNumberTitleText} as="h3">
                                {translate("VAT Number")}
                            </Typography>
                            <Typography {...styles.vatNumberText}>{cart.customerVatNumber}</Typography>
                        </GridItem>
                    )}
                    {/* XNG-Change: add shipping method */}
                    <GridItem {...styles.shippingOtherInformationGridItem}>
                        <GridContainer {...styles.shippingOtherInformationGridContainer}>
                            {(cart.fulfillmentMethod === FulfillmentMethod.Ship || !cart.fulfillmentMethod) && (
                                <GridItem {...styles.shippingInformationCarrierGridItem}>
                                    <Typography {...styles.shippingTitle}>{translate("Shipping Method")}</Typography>
                                    {/* XNG-Change: display account number */}
                                    {parseBoolean(cart.properties?.useShippingAccountNumber) && (
                                        <SmallHeadingAndText
                                            heading={translate("Use Shipping Account Number")}
                                            text={cart.properties["shippingAccountNumber"]}
                                            extendedStyles={styles.shippingAccountNum}
                                        />
                                    )}
                                    <Typography {...styles.shippingCarrierDescription}>
                                        {`${cart.carrier!.description} ${cart.shipVia?.description}`}
                                    </Typography>
                                </GridItem>
                            )}
                            {cart.requestedPickupDate && cart.fulfillmentMethod === FulfillmentMethod.PickUp && (
                                <GridItem {...styles.shippingInformationDateRequestedGridItem}>
                                    <Typography {...styles.dateRequestedTitle}>
                                        {translate("Requested Pickup Date")}
                                    </Typography>
                                    <Typography
                                        {...styles.dateRequestedText}
                                        data-test-selector="orderConfirmation_requestedDate"
                                    >
                                        <LocalizedDateTime dateTime={cart.requestedPickupDateDisplay} />
                                    </Typography>
                                </GridItem>
                            )}
                            {/* XNG-Change display credit card address */}
                            {cart.creditCardBillingAddress && (
                                <GridItem
                                    {...styles.billingInformationCreditCardAddressGridItem}
                                    data-test-selector="creditCardBillingAddress"
                                >
                                    <Typography {...styles.billingCreditCardAddressTitle}>
                                        {translate("Credit Card Billing Address")}
                                    </Typography>
                                    <AddressInfoDisplay
                                        address1={cart.creditCardBillingAddress.address1}
                                        address2={cart.creditCardBillingAddress.address2}
                                        city={cart.creditCardBillingAddress.city}
                                        postalCode={cart.creditCardBillingAddress.postalCode}
                                        state={cart.creditCardBillingAddress.stateAbbreviation}
                                        // XNG-Changes
                                        country={cart.creditCardBillingAddress.countryAbbreviation}
                                    />
                                </GridItem>
                            )}
                            {cart.notes && (
                                <GridItem {...styles.notesGridItem}>
                                    <Typography {...styles.notesTitle}>{translate("Notes")}</Typography>
                                    <Typography
                                        {...styles.notesDescription}
                                        data-test-selector="orderConfirmation_orderNotes"
                                    >
                                        {cart.notes}
                                    </Typography>
                                </GridItem>
                            )}
                            {cart.requestedDeliveryDate &&
                                (cart.fulfillmentMethod === FulfillmentMethod.Ship || !cart.fulfillmentMethod) && (
                                    <GridItem {...styles.shippingInformationDateRequestedGridItem}>
                                        <Typography {...styles.dateRequestedTitle}>
                                            {translate("Requested Delivery Date")}
                                        </Typography>
                                        <Typography
                                            {...styles.dateRequestedText}
                                            data-test-selector="orderConfirmation_requestedDate"
                                        >
                                            <LocalizedDateTime dateTime={cart.requestedDeliveryDateDisplay} />
                                        </Typography>
                                    </GridItem>
                                )}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderConfirmationOrderInformation),
    definition: {
        displayName: "Order Information",
        allowedContexts: ["OrderConfirmationPage"],
        group: "Order Confirmation",
    },
};

export default widgetModule;
